import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Beer, ArrowLeft, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DrinkTicketPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { member } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const isHost = member && (member.is_admin || member.is_super_admin || member.is_venue_admin);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/drink-tickets/${id}`);
      setTicket(res.data);
    } catch {
      setError('Ticket not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleRedeem = async () => {
    if (!member?.id) {
      toast.error('You must be signed in as a tournament host to redeem');
      return;
    }
    if (!window.confirm('Redeem this drink ticket now? This cannot be undone.')) return;
    setRedeeming(true);
    try {
      const res = await axios.post(`${API_URL}/drink-tickets/${id}/redeem`, {
        host_id: member.id,
      });
      setTicket(res.data.ticket);
      toast.success(res.data.already_redeemed ? 'Already redeemed earlier' : 'Ticket redeemed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Redeem failed');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-amber-400 font-heading text-lg">Loading ticket…</div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <Card className="bg-card border-white/10 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Ticket not found</p>
            <p className="text-sm text-muted-foreground mb-6">This ticket link may be invalid or the ticket has been removed.</p>
            <Button onClick={() => navigate('/')} className="bg-secondary text-black hover:bg-secondary/90">Back to TGA</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isRedeemed = ticket.status === 'redeemed';
  const isExpired = ticket.status === 'expired';
  const isActive = ticket.status === 'active';

  const qrValue = `${window.location.origin}/tickets/${ticket.id}`;

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-md mx-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-white mb-4"
          data-testid="ticket-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        {/* The ticket card */}
        <div className={`relative overflow-hidden rounded-2xl border-2 ${
          isRedeemed ? 'border-secondary/40 bg-secondary/5'
          : isExpired ? 'border-zinc-500/40 bg-zinc-500/5'
          : 'border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent'
        }`}>
          {/* Perforation dots */}
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px border-l border-dashed border-white/10 hidden" />

          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Beer className={`w-6 h-6 ${isActive ? 'text-amber-300' : 'text-muted-foreground'}`} />
                <span className={`font-heading text-lg ${isActive ? 'text-white' : 'text-muted-foreground'}`}>
                  Free Drink Ticket
                </span>
              </div>
              <StatusBadge status={ticket.status} />
            </div>

            {isActive && (
              <div className="bg-white p-4 rounded-xl mb-4 mx-auto w-fit" data-testid="ticket-qr">
                <QRCodeSVG value={qrValue} size={200} level="M" />
              </div>
            )}

            {isRedeemed && (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-secondary mx-auto mb-3" />
                <p className="text-white font-medium">Redeemed</p>
                {ticket.redeemed_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(ticket.redeemed_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {isExpired && (
              <div className="text-center py-8">
                <AlertCircle className="w-16 h-16 text-zinc-500 mx-auto mb-3" />
                <p className="text-white font-medium">Expired</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Ticket was valid until {new Date(ticket.expires_at).toLocaleDateString()}
                </p>
              </div>
            )}

            {/* Fallback code + details */}
            <div className="border-t border-dashed border-white/10 pt-4 mt-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Redeem code</span>
                <code
                  className={`font-mono text-lg tracking-widest ${isActive ? 'text-amber-300' : 'text-muted-foreground'}`}
                  data-testid="ticket-fallback-code"
                >
                  {ticket.code}
                </code>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Player</span>
                <span className="text-white">{ticket.member_name} ({ticket.member_number})</span>
              </div>
              {isActive && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Expires</span>
                  <span className="text-white">{new Date(ticket.expires_at).toLocaleDateString()}</span>
                </div>
              )}
              {ticket.sponsor_name && (
                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-muted-foreground">Sponsored by</span>
                  <span className="text-amber-300 font-medium">{ticket.sponsor_name}</span>
                </div>
              )}
            </div>

            {/* Terms & conditions */}
            <div className="border-t border-dashed border-white/10 pt-4 mt-4">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Terms</p>
              <ul className="space-y-1 text-[11px] text-muted-foreground leading-relaxed">
                <li>• Redeemable only at participating breweries</li>
                <li>• Free drink with the purchase of 1 drink (BOGO), on your return visit</li>
                <li>• Free drink must be of equal or lesser value</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Host action */}
        {isActive && isHost && (
          <div className="mt-6">
            <Button
              onClick={handleRedeem}
              disabled={redeeming}
              className="w-full bg-secondary text-black hover:bg-secondary/90 h-12 text-base"
              data-testid="host-redeem-btn"
            >
              {redeeming ? 'Redeeming…' : 'Mark as Redeemed'}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Only tap this when the drink has actually been served
            </p>
          </div>
        )}

        {isActive && !isHost && (
          <p className="text-xs text-center text-muted-foreground mt-6">
            Show this ticket (QR or the code above) to a tournament host to redeem.
          </p>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    active: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    redeemed: 'bg-secondary/20 text-secondary border-secondary/40',
    expired: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40',
  };
  return (
    <span
      className={`px-2 py-1 rounded-full border text-[10px] uppercase tracking-wider font-medium ${styles[status] || styles.expired}`}
      data-testid="ticket-status-badge"
    >
      {status}
    </span>
  );
}
